import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { usePermissions } from '@/contexts/unified-permissions';
import { LoadingState } from '@/components/ui/loading-state';
import { PasswordResetGuard } from '@/components/auth/PasswordResetGuard';
import { useEffect, useRef } from 'react';

export const AuthGuard = () => {
  const { isAuthenticated, permissionsStatus, authReady } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate(); // ✅ Add navigate hook
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastAuthStateRef = useRef<boolean | null>(null);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  // Show loading state until auth system is ready
  if (!authReady || permissionsStatus === 'loading') {
    return (
      <LoadingState 
        title="Authenticating" 
        description="Checking your credentials..." 
        size="md"
        fullScreen={true}
      />
    );
  }

  // Track authentication state changes to prevent rapid redirects
  lastAuthStateRef.current = isAuthenticated;

  // If authenticated, render outlet. Otherwise, redirect to login with debounce.
  if (isAuthenticated) {
    // Clear any pending redirect
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
      redirectTimeoutRef.current = null;
    }

    return (
      <PasswordResetGuard>
        <Outlet />
      </PasswordResetGuard>
    );
  } else {
    // Add small delay to prevent redirect loops during token refresh
    if (!redirectTimeoutRef.current) {
      redirectTimeoutRef.current = setTimeout(() => {
        if (!isAuthenticated && authReady && permissionsStatus === 'ready') {
          navigate('/auth/login', {
            replace: true,
            state: { from: location }
          }); // ✅ Use imperative navigation
        }
      }, 500);
    }

    return (
      <LoadingState 
        title="Verifying access" 
        description="Please wait..." 
        size="sm"
        fullScreen={true}
      />
    );
  }
};
